import React from 'react';
import { cn } from '@/lib/utils';
import { DASHED_REST_LINE } from './RestGapConnector';
import { ControlRow } from '../ControlRow';
import { HelpInfoCallout } from '@/components/config_forms/short_turn/components/HelpInfoCallout';
import { NumberInput } from '@/components/ui/number-input';
import { SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING } from './helpSheetOpticalSpacing';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface RestGapScenario {
  shiftALabel: string;
  shiftADay: string;
  shiftAEndTime: string;
  shiftAHours: number;
  shiftBLabel: string;
  shiftBDay: string;
  shiftBStartTime: string;
  shiftBHours: number;
  restHours: number;
  triggered: boolean;
}

export interface RestGapComparisonProps {
  scenarios: [RestGapScenario, RestGapScenario];
  minimumRestHours: number;
  minimumRestInputValue?: number | null;
  onMinimumRestChange?: (value: number | null) => void;
}

/* ------------------------------------------------------------------ */
/*  ShiftBlock                                                         */
/* ------------------------------------------------------------------ */

const ShiftBlock: React.FC<{
  label: string;
  day: string;
  timeLabel: string;
  timeValue: string;
  triggered: boolean;
  isShiftB?: boolean;
}> = ({ label, day, timeLabel, timeValue, triggered, isShiftB }) => (
  <div
    data-measure-label={`${label} Block`}
    className={cn(
      'relative flex-1 min-w-0 rounded-[0.25rem] border border-border/60 bg-surface-2 px-3 pb-2',
      '[border-top-color:rgba(255,255,255,0.06)]',
      'shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)]',
      isShiftB && triggered && 'border-l-2 border-l-primary',
    )}
  >
    <div
      aria-hidden="true"
      data-measure-label={`${label} Border Top`}
      className="absolute inset-x-0 -top-1 h-2"
    />
    <div
      aria-hidden="true"
      data-measure-label={`${label} Border Bottom`}
      className="absolute inset-x-0 -bottom-1 h-2"
    />
    <div
      aria-hidden="true"
      data-measure-label={`${label} Border Left`}
      className="absolute -left-1 top-0 bottom-0 w-2"
    />
    <div
      aria-hidden="true"
      data-measure-label={`${label} Border Right`}
      className="absolute -right-1 top-0 bottom-0 w-2"
    />
    <div className="mb-1">
      <span className="font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-[1px] text-foreground/70 shrink-0">
        {label}
      </span>
    </div>
    <div className="flex items-center justify-between">
      <div>
        <div className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.5px] text-muted-foreground/30 mb-1">
          Day
        </div>
        <div className="font-body text-[10px] sm:text-[12px] leading-none text-foreground/70 font-medium">
          {day}
        </div>
      </div>
      <div className="text-right">
        <div className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.5px] text-muted-foreground/30 mb-1">
          {timeLabel}
        </div>
        <div className="font-mono text-[13px] leading-none text-foreground/80 font-medium">
          {timeValue}
        </div>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  RestConnector                                                      */
/* ------------------------------------------------------------------ */

const dashedGreenLine = DASHED_REST_LINE;

const RestConnector: React.FC<{
  hours: number;
  diffHours: number;
  minimumRestHours: number;
  triggered: boolean;
  showValues?: boolean;
}> = ({ hours, diffHours, minimumRestHours, triggered, showValues = true }) => (
  <div className="w-[80px] sm:w-[90px] shrink-0 flex flex-col items-center justify-center px-1" data-measure-label="Rest Connector">
    <span
      className={cn(
        'block w-full text-center font-mono text-[10px] sm:text-[12px] leading-tight font-semibold tabular-nums mb-1 shrink-0',
        triggered ? 'text-primary-text' : 'text-scenario-green',
      )}
    >
      {showValues ? `${hours}hr rest` : '\u00A0'}
    </span>
    <div className="relative w-full h-2 -my-1" data-measure-label="Rest Connector Upper Divider">
      <div
        className={cn('pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-px', triggered ? 'bg-primary-text' : 'bg-scenario-green/60')}
        style={triggered ? undefined : dashedGreenLine}
      />
    </div>
    <span
      className={cn(
        'block w-full text-center font-mono text-[10px] sm:text-[12px] leading-tight font-semibold tabular-nums my-1 shrink-0',
        triggered ? 'text-destructive-text' : 'text-scenario-green',
      )}
    >
      {showValues ? `${diffHours}hr ${triggered ? 'short' : 'over'}` : '\u00A0'}
    </span>
    <div className="relative w-full h-2 -my-1" data-measure-label="Rest Connector Lower Divider">
      <div
        className={cn('pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-px', triggered ? 'bg-primary-text' : 'bg-scenario-green/60')}
        style={triggered ? undefined : dashedGreenLine}
      />
    </div>
    <span
      className={cn(
        'block w-full text-center font-mono text-[10px] sm:text-[12px] leading-tight font-medium tabular-nums mt-1 shrink-0',
        triggered ? 'text-primary-text/85' : 'text-scenario-green/80',
      )}
    >
      {showValues ? `${minimumRestHours}hr min` : '\u00A0'}
    </span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  ScenarioRow                                                        */
/* ------------------------------------------------------------------ */

const ScenarioRow: React.FC<{
  scenario: RestGapScenario;
  minimumRestHours: number;
  hideConnectorValues?: boolean;
}> = ({ scenario, minimumRestHours, hideConnectorValues = false }) => {
  const diff = Math.abs(scenario.restHours - minimumRestHours);
  const isShort = scenario.triggered;

  return (
    <div className={cn(!isShort && 'opacity-70')} data-measure-label={isShort ? 'Surcharge Triggered Row' : 'Adequate Rest Row'}>
      {/* Scenario label */}
      <div className="flex items-center gap-2 mb-1">
        <div
          className={cn(
            'w-1.5 h-1.5 rounded-[2px]',
            isShort ? 'bg-primary-text' : 'bg-scenario-green',
          )}
        />
        <span
          className={cn(
            'font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-[1.5px] font-semibold',
            isShort ? 'text-primary-text' : 'text-scenario-green',
          )}
        >
          {isShort ? 'Surcharge Triggered' : 'Adequate Rest'}
        </span>
      </div>

      {/* Timeline row */}
      <div className="flex items-stretch">
        <ShiftBlock
          label={scenario.shiftALabel}
          day={scenario.shiftADay}
          timeLabel="Ends"
          timeValue={scenario.shiftAEndTime}
          triggered={isShort}
        />
        <RestConnector
          hours={scenario.restHours}
          diffHours={diff}
          minimumRestHours={minimumRestHours}
          triggered={isShort}
          showValues={!hideConnectorValues}
        />
        <ShiftBlock
          label={scenario.shiftBLabel}
          day={scenario.shiftBDay}
          timeLabel="Starts"
          timeValue={scenario.shiftBStartTime}
          triggered={isShort}
          isShiftB
        />
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export const RestGapComparison: React.FC<RestGapComparisonProps> = ({
  scenarios,
  minimumRestHours,
  minimumRestInputValue,
  onMinimumRestChange,
}) => {
  const hasExplicitInputValue = typeof minimumRestInputValue === 'number'
    && Number.isFinite(minimumRestInputValue);
  const hasClearedInputValue = minimumRestInputValue === null;
  const hideConnectorValues = hasClearedInputValue;

  return (
    <div role="group" aria-label="Rest gap comparison">
      <ScenarioRow
        scenario={scenarios[0]}
        minimumRestHours={minimumRestHours}
        hideConnectorValues={hideConnectorValues}
      />
      <div className="h-2" data-measure-label="Scenario Divider" aria-hidden="true" />
      <ScenarioRow
        scenario={scenarios[1]}
        minimumRestHours={minimumRestHours}
        hideConnectorValues={hideConnectorValues}
      />
      <HelpInfoCallout
        title="Important"
        className={SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.minimumRest}
      >
        Shifts starting on the same calendar day never trigger a short turn,
        even if the gap between them is short.
      </HelpInfoCallout>
      {onMinimumRestChange && (
        <div className="mt-3 rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
          <ControlRow label="Minimum Rest Period" description="The minimum hours of rest required between shifts. Shorter gaps trigger a surcharge.">
            <NumberInput
              aria-label="Minimum rest period"
              unit="hr"
              size="xs"
              touchTarget
              value={
                hasClearedInputValue
                  ? ''
                  : hasExplicitInputValue
                  ? minimumRestInputValue
                  : (Number.isFinite(minimumRestHours) ? minimumRestHours : '')
              }
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') { onMinimumRestChange(null); return; }
                const parsed = parseFloat(v);
                onMinimumRestChange(Number.isFinite(parsed) ? Math.max(0, parsed) : null);
              }}
              minValue={0}
              step={0.25}
            />
          </ControlRow>
        </div>
      )}
    </div>
  );
};
